import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { minisiteGet, minisitePost } from '../utils/minisiteApi';

// Réponses des invités, visibles uniquement avec le lien d'édition (jeton vérifié côté serveur).
const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const MinisiteRsvpResponses = ({ slug, token, onCount }) => {
  const [responses, setResponses] = useState(null);

  const load = useCallback(() => {
    minisiteGet('getMinisiteRsvps', { slug, token }).then((data) => {
      const list = data?.success ? data.data : [];
      setResponses(list);
      onCount?.(list.length);
    });
  }, [slug, token, onCount]);

  useEffect(() => { load(); }, [load]);

  if (!responses) return <Typography>Chargement…</Typography>;

  const presents = responses.filter((r) => Number(r.presence) === 1);
  const totalPersonnes = presents.reduce((sum, r) => sum + Number(r.nb_personnes || 1) + Number(r.nb_enfants || 0), 0);
  const totalEnfants = presents.reduce((sum, r) => sum + Number(r.nb_enfants || 0), 0);
  // Réponses aux questions libres : « question : réponse », une par ligne.
  const libres = (r) => {
    try {
      return (JSON.parse(r.reponses || '[]') || []).map((x) => `${x.question} : ${x.reponse}`).join('\n');
    } catch (e) {
      return '';
    }
  };

  const handleDelete = (id) => {
    if (!window.confirm('Supprimer cette réponse ?')) return;
    minisitePost('deleteMinisiteRsvp', { slug, token, rsvp_id: id }).then(load);
  };

  // Séparateur « ; » et BOM UTF-8 : s'ouvre directement dans Excel en français.
  const handleExport = () => {
    const header = ['Nom', 'Présence', 'Adultes', 'Enfants', 'Repas', 'Allergies', 'Autres réponses', 'Message', 'Date de réponse'];
    const rows = responses.map((r) => [
      r.nom, Number(r.presence) === 1 ? 'Oui' : 'Non', r.nb_personnes, r.nb_enfants, r.repas, r.allergies, libres(r), r.message, r.created_at,
    ]);
    const csv = [header, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'reponses-invites.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', marginBottom: 2 }}>
        <Typography>
          <strong>{totalPersonnes}</strong> personne{totalPersonnes > 1 ? 's' : ''} présente{totalPersonnes > 1 ? 's' : ''}
          {totalEnfants > 0 ? ` (dont ${totalEnfants} enfant${totalEnfants > 1 ? 's' : ''})` : ''}
          {' · '}{presents.length} oui · {responses.length - presents.length} non
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={load}>Actualiser</Button>
          <Button size="small" variant="outlined" onClick={handleExport} disabled={responses.length === 0}>
            Exporter (CSV)
          </Button>
        </Box>
      </Box>

      {responses.length === 0 ? (
        <Typography color="text.secondary">Aucune réponse pour le moment.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Présence</TableCell>
                <TableCell>Pers.</TableCell>
                <TableCell>Repas</TableCell>
                <TableCell>Allergies</TableCell>
                <TableCell>Autres réponses</TableCell>
                <TableCell>Message</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {responses.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.nom}</TableCell>
                  <TableCell>{Number(r.presence) === 1 ? 'Oui' : 'Non'}</TableCell>
                  <TableCell>
                    {Number(r.presence) === 1 ? `${r.nb_personnes}${Number(r.nb_enfants) > 0 ? ` + ${r.nb_enfants} enf.` : ''}` : '—'}
                  </TableCell>
                  <TableCell>{r.repas}</TableCell>
                  <TableCell>{r.allergies}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-line' }}>{libres(r)}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-line' }}>{r.message}</TableCell>
                  <TableCell>
                    <IconButton size="small" aria-label="Supprimer cette réponse" onClick={() => handleDelete(r.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default MinisiteRsvpResponses;
